import React from 'react';
import { getIn } from 'formik';

import { useParams, useRouteMatch } from 'react-router';
import { useSelector } from 'react-redux';
import TabVideos, { videosSchema } from './TabVideos';
import TabMenus, { menusSchema } from './TabMenus';
import TabPresentation, { presentationSchema } from './TabPresentation';
import TabInfo, { infoSchema } from './TabInfo';
import TabOptions, { optionsSchema } from './TabOptions';
import TabFormat, { formatSchema } from './TabFormat';
import TabSocialMedia, { socialMediaSchema } from './TabSocialMedia';
import Tabs, { Tab } from '../../components/tabs/Tabs';
import { FormRenderProps } from '../../components/item-form/ItemForm';
import { VideoMenu } from '../../types/items';
import yup from '../../utility/yup';
import { selectCurrentUser } from '../../store/modules/users/selectors';
import { TabLinkService } from '../../services/TabLinkService/TabLinkService';
import { findIndexTab } from '../../utility/path';

const videoMenuStorageKey = 'videoMenu.tab';

const saveIndexTab = (indexTab: number) => {
  localStorage.setItem(videoMenuStorageKey, indexTab.toString());
};

const videoMenuTabs = [
  {
    label: 'Info',
    component: TabInfo,
    schema: infoSchema,
  },
  {
    label: 'Videos',
    component: TabVideos,
    schema: videosSchema,
  },
  {
    label: 'Menus',
    component: TabMenus,
    schema: menusSchema,
  },
  {
    label: 'Presentation',
    component: TabPresentation,
    schema: presentationSchema,
  },
  {
    label: 'Options',
    component: TabOptions,
    schema: optionsSchema,
  },
  {
    label: 'Format',
    component: TabFormat,
    schema: formatSchema,
  },
  {
    label: 'Social Media',
    component: TabSocialMedia,
    schema: socialMediaSchema,
  },
];

export const validationSchema = yup.object().dotShape(
  videoMenuTabs.reduce(
    (result, { schema }) => ({
      ...result,
      ...schema,
    }),
    {},
  ),
);

const tabErrorsCount = (schema: object | undefined, touched: object, errors: object): number => (
  (schema && Object.keys(schema).reduce((count, key) => {
    if (getIn(errors, key, false) && getIn(touched, key, false)) {
      return count + 1;
    }
    return count;
  }, 0)) || 0);

const VideoMenuTabs = (formProps: FormRenderProps<VideoMenu>) => {
  const recordId = useParams<{id: string}>().id;
  const { path, url } = useRouteMatch();
  const { data: currentUser } = useSelector(selectCurrentUser);
  const tabLinks = TabLinkService.get('video-menus', { recordId, path, currentUser });
  const tabIndex = findIndexTab(tabLinks, url);
  const tabsConfig = (path.indexOf('/video-menus/new') !== -1) ? [videoMenuTabs[0]] : videoMenuTabs;
  const tabs: Array<Tab<FormRenderProps<VideoMenu>>> = tabsConfig.map((tab) => ({
    label: tab.label,
    component: tab.component,
    badge: {
      variant: 'error',
      value: tabErrorsCount(tab.schema, formProps.touched, formProps.errors),
    },
  }));

  return (
    <Tabs<FormRenderProps<VideoMenu>>
      tabs={tabs}
      tabProps={formProps}
      tabLinks={tabLinks}
      currentTab={tabIndex}
      onChangeTab={saveIndexTab}
    />
  );
};

export default VideoMenuTabs;
